import * as React from "react"
import { useQuery } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { AlertTriangle, ArrowRight, Check, Eye, TrendingDown, TrendingUp } from "lucide-react"

import { DataState } from "@/components/DataState"
import { MetricCard, MetricRow } from "@/components/MetricCard"
import { BarrasHorizontales, Chart } from "@/components/charts/Chart"
import { PageHeader } from "@/components/PageHeader"
import { Toolbar, ToolbarField, ToolbarNote } from "@/components/Toolbar"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { TODAS, alcanceInicialDashboard, paisDeConsulta } from "@/lib/alcance"
import { loQueRequiereAtencion } from "@/lib/atencion"
import type { SenalDeAtencion } from "@/lib/atencion"
import { useAuth } from "@/lib/auth"
import type { Entity } from "@/lib/entities"
import { formatAmount, formatPercent } from "@/lib/format"
import { GRANULARIDADES, agrupar, bucketsDe } from "@/lib/periodo"
import type { Granularidad } from "@/lib/periodo"
import { cn } from "@/lib/utils"

import { fetchClassifyMetrics, fetchIngestSummary } from "@/routes/dashboard/api"
import { SaludCard } from "@/routes/dashboard/SaludCard"
import { RatiosPanel } from "@/routes/dashboard/RatiosPanel"
import { fetchCashflow, fetchPnl, fetchPnlByCountry, fetchRatios } from "@/routes/finance/api"
import { ANIOS_DISPONIBLES } from "@/routes/finance/context"
import { COMPARACIONES, nombreCuenta, nombrePais, serieMensual } from "@/routes/finance/mapping"
import type { Comparacion } from "@/routes/finance/mapping"
import { t } from "@/lib/i18n"

/*
  EL FILTRO DE ÁMBITO SE FUE, Y NO SE SUSTITUYE POR NADA.

  Cesar: «en Dashboard la parte de Ámbito queda raro, debería de ser sin ese
  filtro entre finanzas y contabilidad, y que sí aparezca todo lo que ya tienes,
  y que sólo filtres por períodos de tiempo, comparación y sociedades».

  Tenía razón, y el motivo es que **no era un filtro, era un interruptor para
  esconder media pantalla**. Los otros tres mandos contestan «QUÉ PERÍODO»,
  «CONTRA QUÉ» y «DE QUIÉN» — preguntas sobre las cifras. Éste contestaba «cuánto
  quieres ver», que no es una pregunta que nadie se haga al abrir un dashboard:
  se abre para ver cómo va el negocio, y el negocio es las dos cosas.

  Arrancaba además en «Todo», así que su único efecto posible era quitar. Un
  mando cuyo valor por defecto ya es el mejor, y cuyas otras dos opciones sólo
  ocultan, es ruido en la barra que de verdad importa.
*/
/**
 * Dashboard: SOLO LECTURA, y eso es una decision de producto.
 *
 * No hay un solo control que edite. Se mira para entender como va el negocio;
 * cuando hay que cambiar algo se entra a Finanzas o a Contabilidad, donde cada
 * edicion pide motivo y queda en el audit log. Un panel que ademas deja tocar
 * cifras invita a "corregir" un numero que en realidad viene de un asiento mal
 * clasificado, y esconde el problema en vez de resolverlo.
 *
 * EL PERIODO ES UN FILTRO REAL, no una etiqueta. Se elige granularidad
 * —mes, trimestre, semestre, año— y luego CUAL de esos periodos, y todas las
 * cifras financieras se recalculan sobre esa ventana. Las contables no: son
 * estados actuales (cuantos comprobantes estan sin contabilizar HOY), y
 * fingirlas historicas seria inventar una serie que nadie guarda.
 */
export function DashboardPage() {
  const { activeTenantId, activeEntityId, entities } = useAuth()
  const [anio, setAnio] = React.useState(2026)
  /*
    EL ALCANCE ARRANCA EN LA SOCIEDAD DE ARRIBA, no en «Consolidado».

    Cesar: «los filtros en contabilidad, finanzas y dashboard ya deberían de
    venir aplicados como default a la sociedad que estás viendo». Antes esto
    arrancaba en `""` y la barra de arriba decía una sociedad concreta: dos
    coordenadas contradiciéndose, y ganaba ésta sin decirlo.

    El dashboard es la ÚNICA pantalla que además admite «las cuatro juntas»
    —también de Cesar—, y por eso aquí sigue habiendo un mando y en Finanzas ya
    no. Pero «juntas» es una opción, no el punto de partida.

    Se SIGUE a la sociedad: cambiar el selector de arriba reencuadra el
    dashboard. La excepción es haber elegido «las N juntas» a mano — eso es una
    decisión explícita y no se deshace sola. Ver `lib/alcance.ts`.
  */
  const [alcance, setAlcance] = React.useState(() =>
    alcanceInicialDashboard(entities, activeEntityId))
  React.useEffect(() => {
    setAlcance((actual) =>
      actual === TODAS ? actual : alcanceInicialDashboard(entities, activeEntityId))
  }, [entities, activeEntityId])
  const pais = paisDeConsulta(alcance)
  /*
    ARRANCA EN MENSUAL, no en anual. La lámina 19 pide TRAYECTORIA —«gráficos de
    línea fina; el último dato siempre en rojo»— y con granularidad anual los
    cuatro gráficos del dashboard se reducen a UN punto: no hay trayectoria que
    dibujar, y el último dato en rojo es también el primero. Las tarjetas de
    portada se leen igual de bien con los doce meses sumados.
  */
  const [granularidad, setGranularidad] = React.useState<Granularidad>("mes")
  const [indice, setIndice] = React.useState(0)
  const [comparar, setComparar] = React.useState<Comparacion>("interanual")

  const buckets = bucketsDe(granularidad, anio)
  // Al cambiar de granularidad el indice anterior puede quedar fuera de rango
  // (Q4 -> semestral tiene solo dos). Se acota en la lectura, no con un efecto:
  // un efecto que corrige estado produce un render intermedio con datos de otro
  // periodo.
  const seleccion = Math.min(indice, buckets.length - 1)
  const bucket = buckets[seleccion]

  const pnl = useQuery({
    queryKey: ["finance-pnl", activeTenantId, anio, pais],
    queryFn: () => fetchPnl(anio, pais),
    enabled: Boolean(activeTenantId),
  })
  const caja = useQuery({
    queryKey: ["finance-cashflow", activeTenantId, anio, pais],
    queryFn: () => fetchCashflow(anio, pais),
    enabled: Boolean(activeTenantId),
  })
  const porPais = useQuery({
    queryKey: ["finance-por-pais", activeTenantId, anio],
    queryFn: () => fetchPnlByCountry(anio),
    enabled: Boolean(activeTenantId) && !pais,
  })
  const ratios = useQuery({
    queryKey: ["finance-ratios", activeTenantId, anio, pais, bucket?.meses, comparar],
    queryFn: () => fetchRatios(
      anio, bucket.meses[0], bucket.meses[bucket.meses.length - 1], pais, comparar),
    enabled: Boolean(activeTenantId) && Boolean(bucket),
  })
  // El año anterior, para la comparativa. Es la misma consulta que ya sabemos
  // hacer: no hace falta un endpoint nuevo para comparar dos años.
  const pnlPrevio = useQuery({
    queryKey: ["finance-pnl", activeTenantId, anio - 1, pais],
    queryFn: () => fetchPnl(anio - 1, pais),
    enabled: Boolean(activeTenantId),
  })
  const clasificacion = useQuery({
    queryKey: ["classify-metrics", activeTenantId],
    queryFn: fetchClassifyMetrics,
    enabled: Boolean(activeTenantId),
  })
  const ingesta = useQuery({
    queryKey: ["ingest-summary", activeTenantId],
    queryFn: fetchIngestSummary,
    enabled: Boolean(activeTenantId),
  })

  /** Total de una cuenta dentro del período elegido. */
  function enPeriodo(account: string): number {
    const cuenta = pnl.data?.cuentas.find((c) => c.account === account)
    if (!cuenta) return 0
    return agrupar(serieMensual(cuenta.meses), granularidad, anio)[seleccion]?.valor ?? 0
  }

  const ingresos = enPeriodo("Revenue")
  const cogs = enPeriodo("Cost of Goods Sold (COGS)")
  const equipo = enPeriodo("Labor Costs")
  const gya = enPeriodo("G&A Expenses")
  /*
    EL EBITDA LO DICE EL BACKEND, NO ESTA PANTALLA. Se calculaba aquí como
    ventas − COGS − equipo − G&A mientras el P&L lo leía de `subtotales.ebitda`,
    que es la suma de las cuentas que el plan gerencial marca como EBITDA. Dos
    caminos para el mismo número: el día que el plan cambie qué suma, el
    dashboard y el P&L dirían dos EBITDA distintos y los dos se verían bien.
  */
  const serieEbitda = agrupar(
    serieMensual(pnl.data?.subtotales?.ebitda.meses), granularidad, anio).map((b) => b.valor)
  const ebitda = serieEbitda[seleccion] ?? 0

  const netoCaja = agrupar(serieMensual(caja.data?.neto), granularidad, anio)[seleccion]?.valor ?? 0
  // Saldo, no importe: el acumulado del periodo es el del ULTIMO mes.
  const cajaCierre =
    agrupar(serieMensual(caja.data?.acumulado), granularidad, anio, "ultimo")[seleccion]?.valor ?? 0

  /** Serie de una cuenta agrupada por la granularidad elegida. */
  function serie(datos: typeof pnl.data, account: string): number[] {
    const cuenta = datos?.cuentas.find((c) => c.account === account)
    return agrupar(serieMensual(cuenta?.meses), granularidad, anio).map((b) => b.valor)
  }

  const etiquetas = buckets.map((b) => b.label)

  const serieIngresos = serie(pnl.data, "Revenue")
  const serieCogs = serie(pnl.data, "Cost of Goods Sold (COGS)")
  const serieEquipo = serie(pnl.data, "Labor Costs")
  const serieGya = serie(pnl.data, "G&A Expenses")
  /*
    La serie contra la que se compara.

    `interanual` toma el MISMO bucket del año anterior. `anterior` desplaza la
    propia serie un bucket: el valor de Q3 se compara contra Q2. El primer
    bucket del año se compara contra el último del año anterior, que es lo que
    hace que enero no aparezca como una caída del 100%.
  */
  const serieIngresosAnioPrevio = serie(pnlPrevio.data, "Revenue")
  const serieIngresosPrevio = comparar === "interanual"
    ? serieIngresosAnioPrevio
    : serieIngresos.map((_, i) =>
        i === 0
          ? serieIngresosAnioPrevio[serieIngresosAnioPrevio.length - 1] ?? 0
          : serieIngresos[i - 1])
  const serieCajaAcum = agrupar(
    serieMensual(caja.data?.acumulado), granularidad, anio, "ultimo").map((b) => b.valor)
  const serieNetoCaja = agrupar(serieMensual(caja.data?.neto), granularidad, anio).map((b) => b.valor)

  const docs = ingesta.data?.por_estado ?? {}
  const pendientes = (docs["pendiente"] ?? 0) + (docs["en_revision"] ?? 0)

  /*
    LO QUE REQUIERE TU ATENCIÓN, que hasta hoy era una promesa sin nada detrás.

    Marty abre esta pantalla diciendo «si algo requiere tu atención, va en rojo —
    y sólo una cosa a la vez» y no había NADA que lo cumpliera. Cesar lo notó:
    «pones un comentario al inicio de que si requiere mi atención va en rojo,
    pero no veo nada en rojo, ¿eso sigue funcionando?».

    La regla vive en `lib/atencion.ts`, pura y con sus tests, para poder probar
    el ORDEN —que es la decisión de verdad— sin montar React. No cuesta ninguna
    consulta nueva: se alimenta de las series que esta pantalla ya pinta.
  */
  const atencion = loQueRequiereAtencion({
    cajaAcumulada: serieCajaAcum,
    etiquetas,
    ebitda,
    ingresos,
    sinNormalizar: ingesta.data?.sin_normalizar ?? 0,
    alertaIngesta: ingesta.data?.alerta ?? null,
  })

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow="Dashboard"
        title={t("Cómo va el negocio")}
        description={t("Análisis, sin edición. Las cifras financieras se recalculan sobre el período elegido; las contables son el estado de hoy. Para cambiar algo, se entra a Finanzas o a Contabilidad.")}
        actions={<Badge variant="outline"><Eye />{t("Solo lectura")}</Badge>}
      />

      {/*
        AQUÍ VA EL ROJO, y va ARRIBA DEL TODO. Debajo de las tarjetas sería una
        nota al pie de la pantalla, y lo que dice es precisamente lo que hay que
        leer ANTES que las cifras.

        Sólo se pinta cuando las consultas ya llegaron: durante la carga, `caja`
        e `ingesta` están vacías y la regla devolvería «nada que atender» — un
        «todo en orden» dicho antes de mirar es peor que no decir nada.
      */}
      {!caja.isLoading && !ingesta.isLoading ? <Atencion senal={atencion} /> : null}

      {/*
        LA SALUD DEL CIERRE va debajo del rojo y encima de las cifras: primero lo
        que requiere atención HOY, después cuánto del trabajo del mes está hecho,
        y sólo entonces los números del negocio. Ver `SaludCard`.
      */}
      <SaludCard />

      <Toolbar>
        <div className="hidden self-stretch border-l md:block" aria-hidden="true" />

        <ToolbarField label={t("Año")} htmlFor="anio-dash">
          <Select value={String(anio)} onValueChange={(v) => setAnio(Number(v))}>
            <SelectTrigger id="anio-dash" className="w-24"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ANIOS_DISPONIBLES.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ToolbarField>

        <ToolbarField label={t("Granularidad")} htmlFor="gran-dash">
          <Select
            value={granularidad}
            onValueChange={(v) => { setGranularidad(v as Granularidad); setIndice(0) }}
          >
            <SelectTrigger id="gran-dash" className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              {GRANULARIDADES.map((g) => (
                <SelectItem key={g.id} value={g.id}>{g.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ToolbarField>

        <ToolbarField label={t("Período")} htmlFor="periodo-dash">
          <Select
            value={String(seleccion)}
            onValueChange={(v) => setIndice(Number(v))}
            disabled={buckets.length === 1}
          >
            <SelectTrigger id="periodo-dash" className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              {buckets.map((b, i) => (
                <SelectItem key={b.label} value={String(i)}>{b.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ToolbarField>

        <ToolbarField label={t("Comparar con")} htmlFor="comparar-dash">
          <Select value={comparar} onValueChange={(v) => setComparar(v as Comparacion)}>
            <SelectTrigger id="comparar-dash" className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              {COMPARACIONES.map((c) => (
                <SelectItem key={c.id} value={c.id} title={c.ayuda}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ToolbarField>

        {/*
          SOCIEDADES, NO PAISES. Es la lista real del cliente, con su nombre, y
          no cuatro codigos ISO que hay que traducir de cabeza a empresas.

          El valor sigue siendo el PAIS porque es la dimension que tiene
          `financial_fact` —ver `lib/alcance.ts`—, pero eso es un detalle del
          almacen y no tiene por que salir en pantalla.

          «Las N juntas» solo aparece con mas de una sociedad, y avisa de lo que
          es: una suma sin convertir monedas. La buena esta en Holding.
        */}
        <ToolbarField label={t("Sociedad")} htmlFor="alcance-dash">
          <Select value={alcance} onValueChange={setAlcance}>
            <SelectTrigger id="alcance-dash" className="w-52"><SelectValue /></SelectTrigger>
            <SelectContent>
              {/*
                «TODAS» VA PRIMERA Y SE LLAMA ASÍ. Decía «Las 4 juntas», y Cesar:
                «me parece raro que pongas las 4 juntas, debería de ser Todas o
                algo así». Tenía razón por dos motivos. Un rótulo que lleva el
                NÚMERO de sociedades cambia de texto al dar de alta la quinta
                —«Las 5 juntas»— así que lo que se recuerda de una semana a otra
                deja de estar donde estaba; y «juntas» describe la operación
                (sumarlas) en vez de decir el alcance, que es lo que el resto de
                la lista dice. Va arriba porque es la opción más ancha y porque
                es donde todo el mundo la busca.
              */}
              {agrupadasPorPais(entities).length > 1 ? (
                <SelectItem
                  value={TODAS}
                  title={t("Suma las cifras de todas las sociedades tal cual, en USD. Para el consolidado de verdad —con eliminación de lo que se facturan entre ellas— está Holding.")}
                >
                  {t("Todas las sociedades")}
                </SelectItem>
              ) : null}
              {/*
                AGRUPADAS POR PAIS, y no una entrada por sociedad. No es una
                preferencia: `financial_fact` solo distingue por pais, asi que
                dos sociedades peruanas serian dos opciones con el mismo valor
                —Radix se queda con una— y sobre todo darian la MISMA cifra con
                dos nombres distintos. Cuando eso pase, la entrada lo dice.
              */}
              {agrupadasPorPais(entities).map((g) => (
                <SelectItem key={g.pais} value={g.pais}>
                  <span className="flex items-baseline gap-2">
                    <span className="truncate">
                      {g.sociedades.length === 1
                        ? g.sociedades[0].name || nombrePais(g.pais)
                        : `${nombrePais(g.pais)} · ${g.sociedades.length} sociedades`}
                    </span>
                    <span className="code shrink-0 text-2xs text-muted-foreground">
                      {g.pais.toUpperCase()}
                    </span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ToolbarField>

        <ToolbarNote icon={Eye} className="ml-auto">
          {bucket?.nombre ?? ""}{" "}{t("· USD")}</ToolbarNote>
      </Toolbar>

      <>
          {/*
            CADA CIFRA SE ABRE. Cesar, 2026-10-04: «metiendo más pop ups». La
            tarjeta lleva la miniatura del año y, al pulsarla, el detalle
            período a período en una ventana. Las series son las MISMAS que
            pintan los gráficos de abajo: la ventana no puede decir un número
            distinto del gráfico.
          */}
          <MetricRow>
            <MetricCard label={t("Ventas")} value={ingresos} loading={pnl.isLoading}
                        detalle={{ etiquetas, valores: serieIngresos,
                                   comparar: { nombre: String(anio - 1), valores: serieIngresosAnioPrevio } }} />
            <MetricCard label={t("Costo de ventas")} value={cogs} share={ingresos} loading={pnl.isLoading}
                        detalle={{ etiquetas, valores: serieCogs }} />
            <MetricCard label={t("Costo de equipo")} value={equipo} share={ingresos} loading={pnl.isLoading}
                        detalle={{ etiquetas, valores: serieEquipo }} />
            <MetricCard label={t("EBITDA")} value={ebitda} share={ingresos} loading={pnl.isLoading} tono="destacado"
                        detalle={{ etiquetas, valores: serieEbitda }} />
          </MetricRow>
          <MetricRow className="lg:grid-cols-3">
            <MetricCard label={t("Gastos generales")} value={gya} share={ingresos} loading={pnl.isLoading}
                        detalle={{ etiquetas, valores: serieGya }} />
            <MetricCard label={t("Caja del período")} value={netoCaja} loading={caja.isLoading}
                        detalle={{ etiquetas, valores: serieNetoCaja }} />
            <MetricCard label={t("Caja acumulada al cierre")} value={cajaCierre} loading={caja.isLoading} tono="destacado"
                        detalle={{ etiquetas, valores: serieCajaAcum,
                                   nota: t("Es el movimiento del ejercicio: arranca en cero cada año y no es el saldo bancario.") }} />
          </MetricRow>

          <RatiosPanel
            datos={ratios.data}
            cargando={ratios.isLoading}
            error={ratios.error}
            onRetry={() => void ratios.refetch()}
          />

          <div className="grid gap-2 lg:grid-cols-2">
            <WorkPanel
              title={t("Ingresos y EBITDA")}
              meta={<span className="text-2xs text-muted-foreground">{anio}{" "}{t("· USD")}</span>}
              footnote={t("Dos medidas de la misma escala, un solo eje. Si hiciera falta cruzar ingresos con un porcentaje irían dos gráficos, nunca dos escalas verticales.")}
            >
              <Chart
                etiquetas={etiquetas}
                series={[
                  { nombre: "Ventas", valores: serieIngresos },
                  { nombre: "EBITDA", valores: serieEbitda },
                ]}
              />
            </WorkPanel>

            <WorkPanel
              title={comparar === "interanual"
                ? `Ingresos ${anio} vs. ${anio - 1}`
                : `Ingresos: cada período contra el anterior`}
              meta={
                <span className="text-2xs text-muted-foreground">
                  {ratios.data?.comparacion.etiqueta ?? "—"}
                </span>
              }
              footnote={COMPARACIONES.find((c) => c.id === comparar)?.ayuda}
            >
              <Chart
                tipo="barras"
                etiquetas={etiquetas}
                series={[
                  { nombre: comparar === "interanual" ? String(anio) : "Período",
                    valores: serieIngresos },
                  { nombre: comparar === "interanual" ? String(anio - 1) : "Anterior",
                    valores: serieIngresosPrevio },
                ]}
              />
            </WorkPanel>
          </div>

          {/* `items-start`: el panel de barras tiene cuatro filas y el de al lado un gráfico alto; estirado, las barras quedaban pegadas arriba sobre un vacío. */}
          <div className="grid items-start gap-2 lg:grid-cols-2">
            <WorkPanel
              title={t("Caja acumulada")}
              meta={<span className="text-2xs text-muted-foreground">{anio}{" "}{t("· USD")}</span>}
              footnote={t("Es el movimiento del ejercicio: arranca en cero cada año y no es el saldo bancario. Cruzar por debajo del cero significa que el período se financia con otra cosa.")}
            >
              <Chart
                etiquetas={etiquetas}
                series={[{ nombre: "Caja acumulada", valores: serieCajaAcum }]}
              />
            </WorkPanel>

            {!pais ? (
              <WorkPanel
                title={t("Ingresos por país")}
                meta={<span className="text-2xs text-muted-foreground">{t("Acumulado")}{" "}{anio}</span>}
              >
                <BarrasHorizontales
                  filas={(porPais.data ?? []).map((r) => ({
                    etiqueta: nombrePais(r.pais), valor: r.ingresos,
                  }))}
                />
              </WorkPanel>
            ) : null}
          </div>

          {!pais ? (
            <WorkPanel
              title={t("Rentabilidad por país")}
              meta={<span className="text-2xs text-muted-foreground">{t("Acumulado del año")}{" "}{anio}</span>}
              footnote={t("Este corte es ANUAL aunque el período elegido sea otro: el backend lo devuelve agregado por año. Mostrarlo recortado sin recalcularlo sería mentir sobre la ventana.")}
            >
              <DataState
                loading={porPais.isLoading}
                error={porPais.error}
                empty={(porPais.data ?? []).length === 0}
                emptyMessage={`No hay datos financieros para ${anio}.`}
                onRetry={() => void porPais.refetch()}
                rows={4}
              >
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("País")}</TableHead>
                      <TableHead className="w-32 text-right">{t("Ventas")}</TableHead>
                      <TableHead className="w-28 text-right">{t("% del total")}</TableHead>
                      <TableHead className="w-32 text-right">{t("EBITDA")}</TableHead>
                      <TableHead className="w-24 text-right">{t("Margen")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(porPais.data ?? []).map((r) => (
                      <TableRow key={r.pais}>
                        <TableCell className="font-medium">{nombrePais(r.pais)}</TableCell>
                        <TableCell className="num">{formatAmount(r.ingresos)}</TableCell>
                        <TableCell className="num text-muted-foreground">
                          {r.participacion_ingresos === null
                            ? "" : formatPercent(r.participacion_ingresos, 1)}
                        </TableCell>
                        {/*
                          LAMINA 19: «tablas con filetes finos; EL RESULTADO, Y
                          SOLO EL RESULTADO, en rojo». El EBITDA es el resultado
                          de esta tabla, asi que el rojo vive aqui — y por eso el
                          margen de al lado deja de ser un badge de color.

                          Un badge rojo junto a una cifra roja dice lo mismo dos
                          veces y duplica el acento en la misma fila. El margen
                          se lee del propio porcentaje con su signo; lo que
                          necesitaba era alinearse como numero, no colorearse.
                        */}
                        <TableCell className={cn("num", r.ebitda < 0 && "cifra-atencion")}>
                          {formatAmount(r.ebitda)}
                        </TableCell>
                        <TableCell className="num text-muted-foreground">
                          {r.ebitda_pct === null ? "" : formatPercent(r.ebitda_pct, 1)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </DataState>
            </WorkPanel>
          ) : null}
      </>

      <>
          <MetricRow>
            <MetricCard
              label={t("Comprobantes procesados")}
              value={ingesta.data?.por_estado
                ? Object.values(ingesta.data.por_estado).reduce((s, v) => s + v, 0) : 0}
              loading={ingesta.isLoading}
              integer
            />
            {/*
              DE LAS DOS, EL ROJO ES PARA «SIN NORMALIZAR», y elegir cuál es
              justamente lo que obliga la lámina 18 («un solo dato en rojo por
              vista»). Antes iban las dos en rojo, que es lo mismo que ninguna.

              No son el mismo problema. Una cola de revisión llena es TRABAJO
              ESPERANDO, y el primer mes es grande a propósito —cada corrección
              alimenta la memoria de proveedor—: pintarla de rojo enseña a
              ignorar el rojo. Un archivo sin normalizar, en cambio, llegó y no
              se pudo leer: todavía no es un comprobante, está atascado en la
              puerta, y nadie lo va a encontrar mirando la cola.
            */}
            <MetricCard
              label={t("Sin contabilizar")}
              value={pendientes}
              loading={ingesta.isLoading}
              integer
            />
            <MetricCard
              label={t("Recibidos sin normalizar")}
              value={ingesta.data?.sin_normalizar ?? 0}
              loading={ingesta.isLoading}
              integer
              tono={(ingesta.data?.sin_normalizar ?? 0) > 0 ? "alerta" : "neutro"}
            />
            <MetricCard
              label={t("Corrección humana")}
              value={null}
              loading={clasificacion.isLoading}
              badge={clasificacion.data
                ? formatPercent(clasificacion.data.tasa_correccion_humana, 1)
                : "—"}
            />
          </MetricRow>

          {/*
            AQUÍ HABÍA UNA TABLA «COBERTURA DE LA CLASIFICACIÓN» —qué capa del
            motor (regla, memoria, modelo, humano) resolvió cada comprobante, con
            su confianza media—. Es una métrica de cómo trabaja Marty por dentro,
            no de cómo va la empresa: a quien mira el dashboard no le cambia
            ninguna decisión. Queda la tarjeta «Corrección humana» de arriba, que
            sí dice algo útil: cuánto trabajo manual sigue habiendo.
          */}

          {ingesta.data?.alerta ? (
            <p role="alert"
               className="rounded-md border border-warning/35 bg-warning-soft p-2.5 text-2xs leading-4">
              {ingesta.data.alerta}
            </p>
          ) : null}
      </>
    </div>
  )
}

/**
 * Las sociedades del cliente, agrupadas por pais.
 *
 * Existe porque el selector de alcance del dashboard tiene que ofrecer valores
 * UNICOS y el valor es el pais: es la unica dimension por la que
 * `financial_fact` sabe separar. Ver la nota larga de `lib/alcance.ts`.
 *
 * El orden es el del catalogo, que ya viene alfabetico por razon social.
 */
function agrupadasPorPais(entities: Entity[]): Array<{ pais: string; sociedades: Entity[] }> {
  const porPais = new Map<string, Entity[]>()
  for (const e of entities) {
    if (!e.country) continue
    const lista = porPais.get(e.country)
    if (lista) lista.push(e)
    else porPais.set(e.country, [e])
  }
  return [...porPais.entries()].map(([pais, sociedades]) => ({ pais, sociedades }))
}

/**
 * EL ÚNICO ROJO DE LA PANTALLA.
 *
 * Tres decisiones, y las tres salen de la promesa que hay que cumplir.
 *
 * **Filete rojo a la izquierda, no fondo rojo.** Brandbook: el rojo «señala, no
 * tapiza: nunca más del 5 % de una superficie». Un panel rojo entero grita una
 * vez y después se ignora; un filete de dos píxeles sobre folio se distingue
 * desde el otro lado de la sala sin gritar. Es el mismo gesto que la nota de
 * Marty y que la línea del corte real — y por eso, con esto pintado, en la
 * pantalla NO puede haber otro acento rojo compitiendo.
 *
 * **Siempre lleva botón.** Un aviso sin salida es un reproche. Cada señal sabe
 * dónde se resuelve (`lib/atencion.ts` lo exige y lo prueba).
 *
 * **Cuando no hay nada, se dice.** Es la mitad del arreglo: ver «no hay nada que
 * necesite tu atención» es lo que distingue «lo miré y está bien» de «esto no
 * funciona», que fue exactamente la duda de Cesar. Va en gris y en cuerpo
 * pequeño — es una confirmación, no una noticia.
 */
function Atencion({ senal }: { senal: SenalDeAtencion | null }) {
  if (!senal) {
    return (
      <p className="flex items-center gap-1.5 text-2xs text-muted-foreground">
        <Check className="h-3 w-3 text-success" aria-hidden="true" />
        {t("No hay nada que necesite tu atención ahora mismo.")}
      </p>
    )
  }

  return (
    <section
      role="status"
      className="flex flex-wrap items-start gap-x-3 gap-y-2 rounded-panel border bg-surface py-2.5 pl-3 pr-2.5 shadow-xs"
      style={{ boxShadow: "inset 2px 0 0 0 hsl(var(--margen))" }}
    >
      <AlertTriangle
        className="mt-0.5 h-4 w-4 shrink-0"
        style={{ color: "hsl(var(--margen))" }}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium" style={{ color: "hsl(var(--margen))" }}>
          {t(senal.titulo)}
        </p>
        <p className="mt-0.5 max-w-[80ch] text-2xs leading-5 text-muted-foreground">
          {senal.detalle}
        </p>
      </div>
      <Button size="sm" variant="outline" asChild className="shrink-0">
        <Link to={senal.a}>
          {t(senal.etiquetaAccion)}
          <ArrowRight />
        </Link>
      </Button>
    </section>
  )
}
